import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useModalSelectors } from "../../../app/state/modalSlice/hook/use-modal-selectors";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import {
  addStorageObject,
  fetchAllStorageObjects,
} from "../../../app/state/storageObjectSlice/actions/actions";
import { useStorageSelectors } from "../../../app/state/storageObjectSlice/hook/use-storage-selectors";
import { NormalizedStorageObject } from "../../../services/api/instances/storageApi/types/object_types";
import { SaveFileValue } from "../../../services/api/instances/saveFileApi/types";
import { INITIAL_SCHEMA_VALUE } from "../constants";
import { useAppModals } from "../../../app/state/modalSlice/hook/use-app-modals";
import { setSchemaValue } from "../redux/slice";
import { TAppModal } from "../../../app/state/modalSlice/types";
import { getFileConfig } from "../../Files/helpers";
import { getFileContentByHash } from "../../../services/api/instances/saveFileApi/methods";

export const useCreateSchemaObjectModal = (createSchemaModal: TAppModal) => {
  const dispatch = useAppDispatch();
  const { objects } = useStorageSelectors("userfiles");
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] =
    useState<NormalizedStorageObject<SaveFileValue> | null>(null);
  const [name, setName] = useState<string | null>(null);
  const { handleCloseModal } = useAppModals();
  const filteredSchemas = useMemo(() => {
    return objects.filter(
      (o) => o.value !== null && o.friendly_name.endsWith(".drawio"),
    );
  }, [objects]);

  useEffect(() => {
    if (createSchemaModal.isOpen) {
      dispatch(fetchAllStorageObjects({ service: "userfiles" }));
    }
  }, [createSchemaModal.isOpen]);

  const handleChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const handleSelectSchemaFile = (
    file: NormalizedStorageObject<SaveFileValue> | null,
  ) => {
    setSelectedFile(file);
  };

  const handleModalAction = useCallback(async () => {
    const withCount = name === t("schemas.default_name") || name === null;
    if (selectedFile === null) {
      dispatch(
        addStorageObject({
          name: name ? name : t("schemas.default_name"),
          service: "schemas",
          value: INITIAL_SCHEMA_VALUE,
          objectType: "child",
          currentObjectId: createSchemaModal?.info?.id,
          withoutCount: !withCount,
        }),
      )
        .unwrap()
        .then(() => dispatch(setSchemaValue(INITIAL_SCHEMA_VALUE)));
    } else if (selectedFile) {
      const fileConfig = getFileConfig(
        selectedFile?.value?.userfiles?.filename,
      );
      if (!fileConfig) return;
      const content = await getFileContentByHash(
        selectedFile?.value?.userfiles?.hash,
        fileConfig,
        selectedFile?.value?.userfiles?.filename,
      );
      const xml = typeof content === "string" ? content : "";
      const value = {
        ...INITIAL_SCHEMA_VALUE,
        schemas_object: {
          ...INITIAL_SCHEMA_VALUE.schemas_object,
          schema_xml: xml,
        },
      };
      dispatch(
        addStorageObject({
          name: name ? name : t("schemas.default_name"),
          service: "schemas",
          value,
          objectType: "child",
          currentObjectId: createSchemaModal?.info?.id,
          withoutCount: !withCount,
        }),
      )
        .unwrap()
        .then(() => dispatch(setSchemaValue(value)));
    }
    setName(null);
    setSelectedFile(null);
    handleCloseModal("create_schema_object_modal");
  }, [selectedFile, name, createSchemaModal?.info?.id]);

  return {
    name,
    selectedFile,
    filteredSchemas,
    createSchemaModal,
    handleChangeName,
    handleModalAction,
    handleCloseModal,
    handleSelectSchemaFile,
  };
};
